import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Toast } from "primereact/toast";
import PropTypes from "prop-types";
import { useSettingsStore } from "../store/settingsStore";
import { toOneLine, TOAST_LIFE } from "./toastFormat";
// TODO: Re-enable when notification sound issues are resolved
// import { setStopLineToastRef } from '../data/Services/stopLineAlertService';

// Toast Context
const ToastContext = createContext();

// #430 — choke point único dos toasts com título+corpo (contexto E globalToast).
// Lê o toggle "Notificações em uma linha" em USE time (nunca em module scope):
// OFF (default) → instância regular, payload idêntico ao comportamento
// caracterizado; ON → instância compacta com título+corpo mesclados em uma
// linha (toastFormat.toOneLine), mantendo as durações por severidade.
const DEFAULT_TITLES = {
  success: "Sucesso",
  error: "Erro",
  warn: "Aviso",
  info: "Informação",
};

const dispatchToast = (regularRef, compactRef, severity, message, title) => {
  const resolvedTitle = title ?? DEFAULT_TITLES[severity];
  const oneLine = useSettingsStore.getState()?.oneLineNotifications === true; // USE time
  if (oneLine && compactRef) {
    compactRef.show({
      severity,
      summary: toOneLine(resolvedTitle, message),
      life: TOAST_LIFE[severity],
      className: "p-toast-compact",
    });
    return;
  }
  if (regularRef) {
    regularRef.show({
      severity,
      summary: resolvedTitle,
      detail: message,
      life: TOAST_LIFE[severity],
    });
  }
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const toastRef = useRef(null);
  // Separate instance for compact order-change toasts — positioned top-center
  // while regular notifications stay top-right.
  const toastCompactRef = useRef(null);

  // Set global toast reference when component mounts
  useEffect(() => {
    if (toastRef.current) {
      setGlobalToastRef(toastRef.current);
      // TODO: Re-enable when notification sound issues are resolved
      // setStopLineToastRef(toastRef.current); // Also set for StopLine alerts
    }
    if (toastCompactRef.current) {
      setGlobalCompactToastRef(toastCompactRef.current);
    }
  }, []);

  const showSuccess = useCallback((message, title = "Sucesso") => {
    dispatchToast(
      toastRef.current,
      toastCompactRef.current,
      "success",
      message,
      title,
    );
  }, []);

  const showError = useCallback((message, title = "Erro") => {
    dispatchToast(
      toastRef.current,
      toastCompactRef.current,
      "error",
      message,
      title,
    );
  }, []);

  const showWarning = useCallback((message, title = "Aviso") => {
    dispatchToast(
      toastRef.current,
      toastCompactRef.current,
      "warn",
      message,
      title,
    );
  }, []);

  const showInfo = useCallback((message, title = "Informação") => {
    dispatchToast(
      toastRef.current,
      toastCompactRef.current,
      "info",
      message,
      title,
    );
  }, []);

  // Compact single-line toast — no title/detail split, shrinks to fit.
  // For high-frequency, low-importance notices (e.g. order changes) so a
  // few at once don't take over the screen. Rendered top-center.
  const showCompact = useCallback((message, severity = "info") => {
    if (toastCompactRef.current) {
      toastCompactRef.current.show({
        severity,
        summary: message,
        life: 2500,
        className: "p-toast-compact",
      });
    }
  }, []);

  // Memoize context value to prevent children re-renders when callbacks change
  const value = useMemo(
    () => ({
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showCompact,
    }),
    [showSuccess, showError, showWarning, showInfo, showCompact],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast ref={toastRef} position="top-center" />
      <Toast
        ref={toastCompactRef}
        position="top-center"
        className="p-toast-compact-stack"
      />
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// Global toast instance for use outside React components
let globalToastRef = null;
// Global reference for the compact (top-center) toast instance
let globalCompactToastRef = null;

// Set global toast reference
export const setGlobalToastRef = (ref) => {
  globalToastRef = ref;
};

// Set global compact toast reference
export const setGlobalCompactToastRef = (ref) => {
  globalCompactToastRef = ref;
};

// Global toast functions for use in services
export const globalToast = {
  showSuccess: (message, title = "Sucesso") => {
    dispatchToast(
      globalToastRef,
      globalCompactToastRef,
      "success",
      message,
      title,
    );
  },

  showError: (message, title = "Erro") => {
    dispatchToast(
      globalToastRef,
      globalCompactToastRef,
      "error",
      message,
      title,
    );
  },

  showWarning: (message, title = "Aviso") => {
    dispatchToast(
      globalToastRef,
      globalCompactToastRef,
      "warn",
      message,
      title,
    );
  },

  showInfo: (message, title = "Informação") => {
    dispatchToast(
      globalToastRef,
      globalCompactToastRef,
      "info",
      message,
      title,
    );
  },

  // Compact single-line toast (see showCompact in ToastProvider above).
  showCompact: (message, severity = "info") => {
    if (globalCompactToastRef) {
      globalCompactToastRef.show({
        severity,
        summary: message,
        life: 2500,
        className: "p-toast-compact",
      });
    }
  },
};

// Seam de teste (mesmo padrão de `window.__CHARTS_STORE__` em chartStore.js).
//
// Um e2e NÃO alcança este singleton por `import()` dinâmico: medido em
// 2026-07-27, `import('…/ToastProvider.jsx')` devolveu uma instância de módulo
// diferente da que o app carregou (e diferente de `import('…/ToastProvider')`).
// A cópia nasce com `globalToastRef = null`, então `globalToast.*` vira no-op
// SILENCIOSO e o teste mede um toast que nunca apareceu — falso vermelho.
// Expor aqui garante que o teste dispare pela MESMA instância dos serviços.
if (typeof window !== "undefined") {
  window.__TOAST__ = globalToast;
}
export const toastStyles = `
  .p-toast {
    font-family: var(--font-family);
  }

  .p-toast .p-toast-message {
    border-radius: var(--modal-border-radius);
    box-shadow: var(--modal-shadow);
  }

  .p-toast .p-toast-message-success {
    background-color: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
  }

  .p-toast .p-toast-message-error {
    background-color: #f8d7da;
    border-color: #f5c6cb;
    color: #721c24;
  }

  .p-toast .p-toast-message-warn {
    background-color: #fff3cd;
    border-color: #ffeaa7;
    color: #856404;
  }

  .p-toast .p-toast-message-info {
    background-color: #d1ecf1;
    border-color: #bee5eb;
    color: #0c5460;
  }

  body.dark-mode-custom .p-toast .p-toast-message-success {
    background-color: rgba(40, 167, 69, 0.1);
    border-color: rgba(40, 167, 69, 0.3);
    color: #90ee90;
  }

  body.dark-mode-custom .p-toast .p-toast-message-error {
    background-color: rgba(220, 53, 69, 0.1);
    border-color: rgba(220, 53, 69, 0.3);
    color: #ffb3ba;
  }

  body.dark-mode-custom .p-toast .p-toast-message-warn {
    background-color: rgba(255, 193, 7, 0.1);
    border-color: rgba(255, 193, 7, 0.3);
    color: #ffe066;
  }

  body.dark-mode-custom .p-toast .p-toast-message-info {
    background-color: rgba(23, 162, 184, 0.1);
    border-color: rgba(23, 162, 184, 0.3);
    color: #87ceeb;
  }

  /* StopLine alert styles */
  .p-toast .stop-line-alert {
    border-left: 4px solid #E91E63;
  }

  .p-toast .stop-line-alert .p-toast-icon-close {
    font-size: 14px;
  }

  body.dark-mode-custom .p-toast .stop-line-alert {
    background-color: rgba(233, 30, 99, 0.15);
    border-color: rgba(233, 30, 99, 0.4);
    color: #ffb3c1;
  }
`;
